import { alpha, createTheme } from '@mui/material/styles';

import { AccordionTheme } from './components/Accordion';
import { createBaseline } from './components/Baseline';

const fontFamily = '"Open Sans", sans-serif';

// 62.5% of the browser default (16px) → 10px, so 1.4rem = 14px, 2.2rem = 22px.
const htmlFontSize = 10;

const headingH1 = {
    fontFamily,
    fontSize: '2.2rem',
    fontWeight: 700,
    lineHeight: '2.8rem',
    letterSpacing: 0,
};

const headingH2 = {
    fontFamily,
    fontSize: '1.8rem',
    fontWeight: 700,
    lineHeight: '2.4rem',
    letterSpacing: 0,
};

const headingH4 = {
    fontFamily,
    fontSize: '1.5rem',
    fontWeight: 700,
    lineHeight: '2.2rem',
    letterSpacing: 0,
};

const bodyRegular = {
    fontFamily,
    fontSize: '1.4rem',
    fontWeight: 400,
    lineHeight: '2rem',
    letterSpacing: 0,
};

const bodyEmphasized = {
    fontFamily,
    fontSize: '1.4rem',
    fontWeight: 600,
    lineHeight: '2rem',
    letterSpacing: 0,
};

const status = {
    fontFamily,
    fontSize: '1.2rem',
    fontWeight: 700,
    lineHeight: '1.6rem',
    letterSpacing: 0,
};

const button = {
    fontFamily,
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: '2.2rem',
    letterSpacing: 0,
    textTransform: 'inherit' as const,
};

const caption = {
    fontFamily,
    fontSize: '1.2rem',
    fontWeight: 400,
    lineHeight: '1.6rem',
    letterSpacing: 0,
};

export const theme = createTheme({
    palette: {
        background: {
            default: '#e6eaee',
        },
        common: {
            black: '#000',
            white: '#fff',
        },
        primary: {
            main: '#3F6EB7',
            100: 'rgba(63, 110, 183, 0.10)',
            400: '#5492F4',
            500: '#5CA1FF',
        },
        secondary: {
            main: '#267C9B',
        },
        success: {
            main: '#61C2A2',
            light: '#1CC64B24',
        },
        warning: {
            light: '#FFF6E6',
            main: '#F0C187',
            600: '#FB8C00',
            700: '#E68A00',
            dark: '#B76500',
        },
        error: {
            light: '#FF0000',
            main: '#BF1B28',
        },
        blue: {
            500: '#2196F3',
            main: '#2196F3',
        },
        grey: {
            100: '#F8F8F8',
            200: '#F5F5F5',
            300: '#E9E8E84D',
            400: '#B7B7B7',
            500: '#E9E8E8',
            600: '#787878',
        },
        metrics: {
            positive: '#4BA054',
            negative: '#BF1B28',
            base: '#3F6EB7',
            warning: '#EA7D32',
        },
        text: {
            disabled: '#363636',
        },
    },
    typography: {
        fontFamily,
        htmlFontSize,
        fontSize: 14,
        h1: headingH1,
        h2: headingH2,
        h3: headingH2,
        h4: headingH4,
        h5: headingH4,
        h6: headingH4,
        subtitle1: bodyEmphasized,
        subtitle2: bodyEmphasized,
        body1: bodyRegular,
        body2: bodyRegular,
        button,
        caption,
        overline: {
            ...caption,
            textTransform: 'none',
        },
        status,
    },
    components: {
        MuiCssBaseline: createBaseline({
            fontFamily,
            bodyFontSize: bodyRegular.fontSize,
        }),
        MuiCheckbox: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '&.Mui-checked': {
                        color: theme.palette.primary.main,
                    },
                    '&.Mui-disabled': {
                        color: alpha(theme.palette.common?.black, 0.2),
                    },
                }),
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '&.Mui-disabled': {
                        '& .MuiFormControlLabel-label': {
                            color: alpha(theme.palette.common?.black, 0.2),
                        },
                    },
                }),
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    padding: '8px 22px',
                    fontSize: button.fontSize,
                    fontWeight: button.fontWeight,
                    letterSpacing: 0,
                    textTransform: 'inherit',
                },
            },
            variants: [
                {
                    props: { size: 'small' },
                    style: {
                        height: '30px',
                        padding: '8px 10px',
                    },
                },
            ],
        },
        MuiFormLabel: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '&.Mui-disabled': {
                        color: alpha(theme.palette.common?.black, 0.6),
                        TextFillColor: alpha(theme.palette.common?.black, 0.6),
                    },
                }),
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: ({ theme }) => ({
                    fontSize: bodyRegular.fontSize,
                    letterSpacing: 0,
                    '&.Mui-disabled>input': {
                        backgroundColor: '#F8F8F8',
                        color: alpha(theme.palette.common?.black, 0.8),
                        TextFillColor: alpha(theme.palette.common?.black, 0.8),
                        WebkitTextFillColor: alpha(
                            theme.palette.common?.black,
                            0.8,
                        ),
                    },
                    '&.Mui-disabled fieldset.MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.common?.black, 0.1),
                    },
                }),
                input: {
                    fontSize: bodyRegular.fontSize,
                    letterSpacing: 0,
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    fontSize: bodyRegular.fontSize,
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontSize: bodyRegular.fontSize,
                    letterSpacing: 0,
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    fontSize: bodyRegular.fontSize,
                    letterSpacing: 0,
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontSize: bodyRegular.fontSize,
                    letterSpacing: 0,
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                inputRoot: {
                    fontSize: bodyRegular.fontSize,
                },
                option: {
                    fontSize: bodyRegular.fontSize,
                    letterSpacing: 0,
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    fontFamily,
                    fontSize: bodyEmphasized.fontSize,
                    fontWeight: bodyEmphasized.fontWeight,
                    letterSpacing: 0,
                    textTransform: 'none',
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    fontFamily,
                    fontSize: bodyEmphasized.fontSize,
                    fontWeight: bodyEmphasized.fontWeight,
                    letterSpacing: 0,
                    textTransform: 'none',
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    fontFamily,
                    fontSize: caption.fontSize,
                    fontWeight: 600,
                    lineHeight: caption.lineHeight,
                    letterSpacing: 0,
                },
            },
        },
        MuiChip: {
            variants: [
                {
                    props: { variant: 'pill' },
                    style: {
                        height: 'auto',
                        minHeight: 0,
                        borderRadius: '22px',
                        padding: '4px 8px',
                        fontFamily: status.fontFamily,
                        fontSize: status.fontSize,
                        fontWeight: status.fontWeight,
                        lineHeight: status.lineHeight,
                        letterSpacing: 0,
                        '& .MuiChip-label': {
                            padding: 0,
                            fontFamily: status.fontFamily,
                            fontSize: status.fontSize,
                            fontWeight: status.fontWeight,
                            lineHeight: status.lineHeight,
                        },
                    },
                },
            ],
            styleOverrides: {
                root: ({ ownerState }) =>
                    ownerState.variant === 'pill'
                        ? {
                              height: 'auto',
                              minHeight: 0,
                              borderRadius: '22px',
                              padding: '4px 8px',
                              fontFamily: status.fontFamily,
                              fontSize: status.fontSize,
                              fontWeight: status.fontWeight,
                              lineHeight: status.lineHeight,
                              letterSpacing: 0,
                              [`& .MuiChip-label`]: {
                                  padding: 0,
                                  fontFamily: status.fontFamily,
                                  fontSize: status.fontSize,
                                  fontWeight: status.fontWeight,
                                  lineHeight: status.lineHeight,
                              },
                          }
                        : {},
            },
        },
        ...AccordionTheme,
    },
    breakpoints: {
        values: {
            mobile: 0,
            tablet: 768,
            laptop: 1024,
            desktop: 1200,
        },
    },
});
